// Yard visits (Suresh 2026-10-06): every vehicle through the gate gets
// ONE reason — tipping, outgoing load, delivery, staff, site cleanup
// (or "not a vehicle" for text the camera misreads). Hard evidence is
// assigned automatically; medium evidence is pre-filled as a
// suggestion; everything else waits for a person. Default view =
// Needs reason. Known vehicles (staff cars, trailers…) live on the
// Reference tab. Data: /api/yard/visits; photos: /api/verkada/image.
//
// Trips (2026-10-07): a row is one TRIP (a plate can come 3 times a day);
// each trip is matched to exactly one tip / outgoing load and each log
// entry to at most one trip. Two exception lists close the loop:
//   📭 No log entry      — a trip that tipped / took a load with nothing logged
//   👻 Not seen by camera — a tip / load no camera trip accounts for
import { useEffect, useRef, useState } from 'react';

export const REASON_META = {
  tipping:     { icon: '🚚', label: 'Tipping',      bg: '#dcfce7', fg: '#166534' },
  outgoing:    { icon: '📤', label: 'Outgoing load', bg: '#dbeafe', fg: '#1e40af' },
  delivery:    { icon: '📦', label: 'Delivery',     bg: '#fef3c7', fg: '#92400e' },
  visitor:     { icon: '🙋', label: 'Visitor',      bg: '#ecfeff', fg: '#155e75' },
  staff:       { icon: '👷', label: 'Staff',        bg: '#f3f4f6', fg: '#374151' },
  cleanup:     { icon: '🧹', label: 'Site cleanup', bg: '#ede9fe', fg: '#5b21b6' },
  not_vehicle: { icon: '🔤', label: 'Not a vehicle', bg: '#f9fafb', fg: '#9ca3af' }
};
const ORDER = ['tipping', 'outgoing', 'delivery', 'visitor', 'staff', 'cleanup', 'not_vehicle'];
const ALWAYS_OK = ['staff', 'delivery', 'visitor', 'cleanup', 'not_vehicle'];

// Image popup (2026-10-07): photos open in an in-page overlay instead
// of a new browser tab. Click anywhere or press Esc to close; ← / →
// flip between the photos passed in (e.g. plate + vehicle).
function Lightbox({ images, index = 0, onClose }) {
  const [i, setI] = useState(index);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI(x => Math.min(images.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);
  const cur = images[i] || images[0];
  if (!cur) return null;
  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
      <img src={cur.src} alt={cur.title || ''} onClick={e => e.stopPropagation()}
           style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6, background: '#111', cursor: 'default' }} />
      <div onClick={e => e.stopPropagation()} style={{ color: '#fff', marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', cursor: 'default' }}>
        {images.length > 1 && <button className="refresh-btn small" disabled={i === 0} onClick={() => setI(i - 1)}>←</button>}
        <span>{cur.title || ''}{images.length > 1 ? '  (' + (i + 1) + '/' + images.length + ')' : ''}</span>
        {images.length > 1 && <button className="refresh-btn small" disabled={i === images.length - 1} onClick={() => setI(i + 1)}>→</button>}
        <button className="refresh-btn small" onClick={onClose}>✕ Close</button>
      </div>
    </div>
  );
}

// Picker (2026-10-07): clicking Tipping / Outgoing shows that day's
// tips / loads not yet matched to any vehicle, closest in time first.
// Tips show the driver's plate photo next to the camera photo so the
// match can be confirmed by eye.
function MatchPicker({ visit, kind, onClose, onPick, onZoom }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showOthers, setShowOthers] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/yard/visits?candidates=' + kind + '&date=' + visit.date + '&plate=' + encodeURIComponent(visit.plate) + '&trip=' + (visit.tripStart || 0), { credentials: 'include' });
        const j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j.error || ('HTTP ' + r.status));
        setData(j);
      } catch (e) { setErr(e.message); }
    })();
  }, [visit.date, visit.plate, visit.tripStart, kind]);
  const camImg = (k) => '/api/verkada/image?date=' + visit.date + '&plate=' + encodeURIComponent(visit.plate) + '&kind=' + k;
  const inWindow = (dt) => dt != null && (kind === 'tipping' ? dt >= 1 && dt <= 14 : dt >= -10 && dt <= 45);
  const Row = ({ c }) => (
    <tr style={{ background: inWindow(c.dtMin) ? '#f0fdf4' : undefined }}>
      {kind === 'tipping' && (
        <td style={{ width: 110 }}>
          {c.photo
            ? <a href={c.photo} onClick={e => { e.preventDefault(); onZoom([{ src: c.photo, title: 'Driver photo — ' + (c.plate || '') + ' · ' + (c.who || '') }]); }}><img src={c.photo} alt={c.plate} loading="lazy"
                 style={{ height: 64, maxWidth: 104, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                 onError={e => { e.currentTarget.replaceWith('no photo'); }} /></a>
            : <span className="small" style={{ color: '#aaa' }}>no photo</span>}
        </td>
      )}
      <td><code style={{ fontWeight: 700, fontSize: '1.05em' }}>{c.plate || '(no plate)'}</code></td>
      <td>{c.who || ''}</td>
      <td>{String(c.time || '').slice(0, 5)}</td>
      <td className="num" style={{ color: inWindow(c.dtMin) ? '#15803d' : '#888', fontWeight: inWindow(c.dtMin) ? 700 : 400 }}>
        {c.dtMin == null ? '' : (c.dtMin >= 0 ? '+' : '') + c.dtMin + ' min'}
      </td>
      <td className="small" style={{ color: '#b45309' }}>{c.claimedBy ? 'matched to ' + c.claimedBy : ''}</td>
      <td><button className="refresh-btn small" style={{ fontWeight: 700 }}
            onClick={() => (!c.claimedBy || window.confirm('This ' + (kind === 'tipping' ? 'tip' : 'load') + ' is already matched to ' + c.claimedBy + '. Match it here instead?')) && onPick(c)}>
            Pick</button></td>
    </tr>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '5vh' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: '1rem 1.25rem', width: 'min(920px, 94vw)', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: '0.6rem' }}>
          {visit.hasImg && <img src={camImg('plate')} alt={visit.plate} style={{ height: 80, borderRadius: 4, cursor: 'zoom-in' }}
                               onClick={() => onZoom([...(visit.hasImg ? [{ src: camImg('plate'), title: 'Camera plate — ' + visit.shown }] : []), ...(visit.hasVimg ? [{ src: camImg('vehicle'), title: 'Camera vehicle — ' + visit.shown }] : [])], 0)} />}
          {visit.hasVimg && <img src={camImg('vehicle')} alt="vehicle" style={{ height: 80, borderRadius: 4, cursor: 'zoom-in' }}
                               onClick={() => onZoom([...(visit.hasImg ? [{ src: camImg('plate'), title: 'Camera plate — ' + visit.shown }] : []), ...(visit.hasVimg ? [{ src: camImg('vehicle'), title: 'Camera vehicle — ' + visit.shown }] : [])], visit.hasImg ? 1 : 0)} />}
          <div>
            <div style={{ fontSize: '1.1em' }}>{REASON_META[kind].icon} Match <code style={{ fontWeight: 700 }}>{visit.shown}</code> to {kind === 'tipping' ? 'a tip' : 'an outgoing load'}</div>
            <div className="small" style={{ color: '#666' }}>
              Camera {visit.date} {(visit.first || '').slice(0, 5)}{visit.last && visit.last !== visit.first ? '–' + visit.last.slice(0, 5) : ''}
              {(visit.tripsOfDay || 1) > 1 ? ' (trip ' + visit.tripNo + ' of ' + visit.tripsOfDay + ' today)' : ''}
              {' · '}{kind === 'tipping' ? 'tips are usually logged 1–14 min after the camera' : 'loads are usually logged 0–45 min after the trailer passes'} (green)
            </div>
          </div>
        </div>
        {err && <div className="error small">{err}</div>}
        {!data && !err && <div className="small">Loading {kind === 'tipping' ? 'tips' : 'loads'}…</div>}
        {data && (
          <>
            <div style={{ fontWeight: 700, margin: '0.3rem 0' }}>Unmatched {kind === 'tipping' ? 'tips' : 'outgoing loads'} on {visit.date} ({data.unmatched.length})</div>
            {data.unmatched.length === 0
              ? <div className="small" style={{ color: '#666' }}>Every {kind === 'tipping' ? 'tip' : 'load'} that day is already matched to a vehicle.</div>
              : <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
                  <thead><tr>{kind === 'tipping' && <th>Driver photo</th>}<th>Plate</th><th>{kind === 'tipping' ? 'Customer' : 'Hauler → destination'}</th><th>Logged</th><th className="num">vs camera</th><th></th><th></th></tr></thead>
                  <tbody>{data.unmatched.map(c => <Row key={c.key} c={c} />)}</tbody>
                </table>}
            {data.matchedElsewhere.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <button className="link" onClick={() => setShowOthers(x => !x)}>
                  {showOthers ? 'Hide' : 'Show'} {data.matchedElsewhere.length} already matched to other vehicles
                </button>
                {showOthers && (
                  <table className="fs-table tidy" style={{ fontSize: '0.88em', marginTop: '0.3rem', opacity: 0.85 }}>
                    <tbody>{data.matchedElsewhere.map(c => <Row key={'m' + c.key} c={c} />)}</tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: '0.8rem' }}>
          <button className="refresh-btn small" onClick={() => onPick(null)}
            title={'Mark as ' + REASON_META[kind].label + ' without linking a specific ' + (kind === 'tipping' ? 'tip' : 'load')}>
            {REASON_META[kind].icon} Nothing logged — mark {REASON_META[kind].label} anyway (goes to 📭 No log entry)
          </button>
          <button className="refresh-btn small" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
const iso = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
const daysAgo = (n) => iso(new Date(Date.now() - n * 86400e3));
const PRESETS = [['Today', 0], ['7 days', 6], ['14 days', 13], ['30 days', 29], ['3 months', 91]];

function Chip({ active, color, children, onClick }) {
  return (
    <button onClick={onClick} className="refresh-btn small"
      style={{ borderColor: active ? color : undefined, boxShadow: active ? 'inset 0 0 0 1px ' + color : undefined, fontWeight: active ? 700 : 400 }}>
      {children}
    </button>
  );
}

export default function YardVisits() {
  const [from, setFrom] = useState(daysAgo(13));
  const [to, setTo] = useState(iso(new Date()));
  const [status, setStatus] = useState('needs');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);       // date|plate showing reason buttons
  const [always, setAlways] = useState({});           // date|plate -> bool
  const [picker, setPicker] = useState(null);         // {visit, kind}
  const [zoom, setZoom] = useState(null);             // {images:[{src,title}], index}
  const [sel, setSel] = useState(new Set());          // selected 'date|plate' keys (bulk)
  // Rows whose vehicle photo was pre-loaded on hover (popup opens instantly)
  const prefetched = useRef(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => { fetch('/api/whoami', { credentials: 'include' }).then(r => r.json()).then(j => setIsAdmin(!!j.isAdmin)).catch(() => {}); }, []);
  const [limit, setLimit] = useState(50);

  async function load(off = 0) {
    setBusy(true); setErr(null);
    try {
      const qs = new URLSearchParams({ from, to, status, limit: String(limit), offset: String(off) });
      if (q.trim()) qs.set('q', q.trim());
      const r = await fetch('/api/yard/visits?' + qs, { credentials: 'include' });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || ('HTTP ' + r.status));
      setData(j); setOffset(off);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  useEffect(() => { setSel(new Set()); load(0); }, [from, to, status, limit]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function post(body) {
    const r = await fetch('/api/yard/visits', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || ('HTTP ' + r.status));
    return j;
  }
  const rowKey = (v) => v.date + '|' + v.plate + '|' + (v.tripStart || 0);
  async function save(v, reason, target, note, targetId) {
    const key = rowKey(v);
    let owner = null;
    const alw = !!always[key] && ALWAYS_OK.includes(reason);
    if (alw) { owner = window.prompt('Always ' + REASON_META[reason].label + ' — whose vehicle / which company? (optional)', ''); if (owner === null) return; }
    try {
      // Same plate pending on other days? Offer to apply to all of them.
      if (ALWAYS_OK.includes(reason) && (v.platePending || 0) > 1 &&
          window.confirm(v.shown + ' is waiting on ' + v.platePending + ' days in this period.\n\nOK = mark ALL ' + v.platePending + ' days ' + REASON_META[reason].label + '\nCancel = just this day')) {
        await post({ action: 'plate-all', plate: v.plate, from, to, reason, always: alw, owner: owner || null, note: note || null });
      } else {
        await post({ date: v.date, plate: v.plate, trip: v.tripStart || 0, reason, target: target || null, targetId: targetId || null, note: note || null, always: alw, owner: owner || null });
      }
      setEditing(null); load(offset);
    }
    catch (e) { alert(e.message); }
  }
  function setReason(v, reason) {
    // Tipping / Outgoing open the picker of that day's unmatched tips / loads
    if (reason === 'tipping' || reason === 'outgoing') { setPicker({ visit: v, kind: reason }); return; }
    save(v, reason, null, null);
  }
  function picked(c) {
    const { visit, kind } = picker;
    setPicker(null);
    if (!c) return save(visit, kind, null, 'no specific ' + (kind === 'tipping' ? 'tip' : 'load'));
    const note = (kind === 'tipping' ? 'tip ' : 'load ') + (c.plate || '(no plate)') + ' · ' + (c.who || '') + ' · ' + String(c.time || '').slice(0, 5) + (c.plate ? '' : ' · id ' + c.id);
    save(visit, kind, c.plate || null, note, c.key);
  }
  async function accept(v) {
    try { await post({ date: v.date, plate: v.plate, trip: v.tripStart || 0, reason: v.reason, target: v.target, targetId: v.targetId || null, note: 'accepted: ' + v.evidence }); load(offset); }
    catch (e) { alert(e.message); }
  }
  async function acceptAll() {
    const n = data ? data.summary.suggested : 0;
    if (!n || !window.confirm('Accept all ' + n + ' suggestion(s) for ' + from + ' → ' + to + '?')) return;
    setBusy(true);
    try { const j = await post({ action: 'accept', from, to }); alert('Accepted ' + j.accepted + '.'); load(0); }
    catch (e) { alert(e.message); setBusy(false); }
  }
  async function bulkMark(reason) {
    const items = [...new Set([...sel].map(k => k.split('|').slice(0, 2).join('|')))].map(k => { const [date, plate] = k.split('|'); return { date, plate }; });
    if (!items.length) return;
    if (!window.confirm('Mark ' + items.length + ' selected visit(s) as ' + REASON_META[reason].label + '?')) return;
    setBusy(true);
    try { const j = await post({ action: 'bulk', items, reason }); setSel(new Set()); alert('Updated ' + j.updated + '.'); load(offset); }
    catch (e) { alert(e.message); setBusy(false); }
  }
  const toggleSel = (k) => setSel(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  // Admin: clear saved machine-made tags so the current matching logic
  // redoes them (human decisions are never touched).
  async function recompute() {
    const all = window.confirm(
      'Re-run matching with the current logic?\n\n' +
      'OK = outgoing / trailer matches only (recommended)\n' +
      'Cancel = choose again');
    let scope = 'outgoing';
    if (!all) {
      if (!window.confirm('Clear ALL machine-made tags instead (partial reads, time matches, text reads too)?\nYour own decisions are never touched.')) return;
      scope = 'all-auto';
    }
    setBusy(true);
    try {
      const j = await post({ action: 'recompute', scope });
      alert('Cleared ' + j.cleared + ' saved machine-made tag(s)' + (j.trailers && j.trailers.trailersLearned != null ? '; re-learned ' + j.trailers.trailersLearned + ' trailer(s) → carrier' : '') + '. The list now shows the new matching.');
      load(0);
    } catch (e) { alert(e.message); setBusy(false); }
  }
  async function undo(v) {
    try {
      const r = await fetch('/api/yard/visits?date=' + v.date + '&plate=' + encodeURIComponent(v.plate) + '&trip=' + (v.tripStart || 0), { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      load(offset);
    } catch (e) { alert(e.message); }
  }

  const photos = (v) => [
    ...(v.hasImg ? [{ src: img(v, 'plate'), title: 'Plate — ' + v.shown + ' · ' + v.date + ' ' + (v.first || '').slice(0, 5) }] : []),
    ...(v.hasVimg ? [{ src: img(v, 'vehicle'), title: 'Vehicle — ' + v.shown + ' · ' + v.date + ' ' + (v.first || '').slice(0, 5) }] : [])
  ];
  const img = (v, kind) => '/api/verkada/image?date=' + v.date + '&plate=' + encodeURIComponent(v.plate) + '&kind=' + kind;
  const s = data && data.summary;
  return (
    <div className="page">
      {picker && <MatchPicker visit={picker.visit} kind={picker.kind} onClose={() => setPicker(null)} onPick={picked} onZoom={(images, index) => setZoom({ images, index: index || 0 })} />}
      {zoom && <Lightbox images={zoom.images} index={zoom.index} onClose={() => setZoom(null)} />}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>🚧 Yard visits</h3>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {PRESETS.map(([label, n]) => (
              <button key={label} className="refresh-btn small" onClick={() => { setFrom(daysAgo(n)); setTo(iso(new Date())); }}>{label}</button>
            ))}
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
            <span>→</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
            <select value={limit} onChange={e => setLimit(Number(e.target.value))} title="Rows per page">
              <option value={50}>50 / page</option><option value={100}>100 / page</option><option value={200}>200 / page</option>
            </select>
            {isAdmin && <button className="refresh-btn small" onClick={recompute} disabled={busy} title="Clear saved machine-made tags and redo them with the current matching logic">⟳ Re-run matching</button>}
            <input placeholder="plate…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(0)} style={{ width: 100 }} />
          </div>
        </div>
        <div className="note">Every trip through the gate needs one reason, and every tip / outgoing load should match exactly one camera trip. Clear matches are filled in automatically; likely ones are suggested; the rest wait for you. Known vehicles (staff cars, trailers) are managed on the Reference tab.</div>
        {err && <div className="error small">{err}</div>}

        {s && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0.5rem 0' }}>
            <Chip active={status === 'needs'} color="#b91c1c" onClick={() => setStatus('needs')}>
              <span style={{ color: s.needs ? '#b91c1c' : '#15803d', fontWeight: 700 }}>❓ Needs reason {s.needs}</span>
            </Chip>
            <Chip active={status === 'suggested'} color="#b45309" onClick={() => setStatus('suggested')}>💡 Suggested {s.suggested}</Chip>
            {s.suggested > 0 && <button className="refresh-btn small" style={{ fontWeight: 700 }} onClick={acceptAll} disabled={busy}>✓ Accept all suggestions</button>}
            <span style={{ width: 12 }} />
            {ORDER.map(r => (
              <Chip key={r} active={status === r} color={REASON_META[r].fg} onClick={() => setStatus(r)}>
                {REASON_META[r].icon} {REASON_META[r].label} {s.counts[r] || 0}
              </Chip>
            ))}
            <Chip active={status === 'all'} color="#374151" onClick={() => setStatus('all')}>All {s.total}</Chip>
          </div>
        )}
        {s && s.log != null && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 0.5rem', padding: '0.35rem 0.6rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <span title="Each tip / outgoing load matched to exactly one camera trip">
              🔗 <strong>{s.matchedLog}</strong> of {s.log} tips &amp; loads matched to a camera trip
            </span>
            <span style={{ width: 8 }} />
            <Chip active={status === 'unlogged'} color="#b91c1c" onClick={() => setStatus('unlogged')}>
              <span title="Trips that tipped / took a load but nothing is left on the log for them (missed ticket?)">📭 No log entry {s.unlogged || 0}</span>
            </Chip>
            <Chip active={status === 'unseen'} color="#7c3aed" onClick={() => setStatus('unseen')}>
              <span title="Tips / loads on the log that no camera trip accounts for">👻 Not seen by camera {(s.unseen.tipping || 0) + (s.unseen.outgoing || 0)}</span>
            </Chip>
            {s.unseen.cameraOff > 0 && <span className="small" style={{ color: '#888' }}>(+{s.unseen.cameraOff} on days the camera recorded nothing)</span>}
            {(s.duplicates || 0) > 0 && (
              <Chip active={status === 'duplicate'} color="#0f766e" onClick={() => setStatus('duplicate')}>
                <span title="The same truck read again under another plate string, or a trailer passing with its cab — shown, but counted once">♊ Second reads {s.duplicates}</span>
              </Chip>
            )}
          </div>
        )}

        {data && data.rows.length === 0 && (
          <div style={{ color: '#15803d', fontWeight: 700, padding: '0.75rem 0' }}>
            {status === 'needs' ? 'Every trip in this period has a reason. ✓' : status === 'unseen' ? 'Every tip and load in this period was seen by the camera. ✓' : 'Nothing here for this period.'}
          </div>
        )}
        {data && data.status === 'unseen' && data.rows.length > 0 && (
          <table className="fs-table tidy" style={{ fontSize: '0.88em' }}>
            <thead><tr><th>Date</th><th>Logged</th><th>Type</th><th>Plate / truck</th><th>Customer / hauler</th><th></th></tr></thead>
            <tbody>
              {data.rows.map(u => (
                <tr key={u.key}>
                  <td>{u.date}</td>
                  <td>{String(u.time || '').slice(0, 5)}</td>
                  <td>{REASON_META[u.kind].icon} {u.kind === 'tipping' ? 'Tip' : 'Outgoing load'}</td>
                  <td><code>{u.plate || '(no plate)'}</code></td>
                  <td>{u.who || ''}</td>
                  <td className="small" style={{ color: '#888' }}>{u.cameraOff ? 'camera recorded nothing that day' : 'no camera trip matched — camera missed it, or the plate on the log is wrong'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {data && data.status !== 'unseen' && data.rows.length > 0 && sel.size > 0 && (
          <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '0.4rem 0.6rem', marginBottom: '0.4rem', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{sel.size} selected</strong> — mark as:
            {['delivery', 'visitor', 'staff', 'cleanup', 'not_vehicle'].map(r => (
              <button key={r} className="refresh-btn small" disabled={busy} onClick={() => bulkMark(r)}>{REASON_META[r].icon} {REASON_META[r].label}</button>
            ))}
            <button className="link" onClick={() => setSel(new Set())}>clear</button>
            <span className="small" style={{ color: '#666' }}>(tips / loads are matched one at a time with the picker)</span>
          </div>
        )}
        {data && data.status !== 'unseen' && data.rows.length > 0 && (
          <table className="fs-table tidy" style={{ fontSize: '0.88em' }}>
            <thead><tr>
              <th style={{ width: 24 }}><input type="checkbox" title="Select all on this page"
                checked={data.rows.length > 0 && data.rows.every(v => sel.has(rowKey(v)))}
                onChange={e => setSel(prev => { const n = new Set(prev); for (const v of data.rows) { const k = rowKey(v); e.target.checked ? n.add(k) : n.delete(k); } return n; })} /></th>
              <th>Photo</th><th>Plate</th><th>Date</th><th>Time</th><th>Reason</th><th>Why</th><th></th></tr></thead>
            <tbody>
              {data.rows.map(v => {
                const key = rowKey(v);
                const m = v.reason ? REASON_META[v.reason] : null;
                const open = v.status === 'needs' || editing === key;
                return (
                  <tr key={key} style={{ background: sel.has(key) ? '#eff6ff' : v.status === 'needs' ? '#fff7f7' : undefined }}
                      onMouseEnter={() => { if (v.hasVimg && !prefetched.current.has(key)) { prefetched.current.add(key); const im = new Image(); im.src = img(v, 'vehicle'); } }}>
                    <td><input type="checkbox" checked={sel.has(key)} onChange={() => toggleSel(key)} /></td>
                    <td>{v.hasImg
                      ? <a href={img(v, 'plate')} onClick={e => { e.preventDefault(); setZoom({ images: photos(v), index: 0 }); }} style={{ cursor: 'zoom-in' }}>
                          <img src={img(v, 'plate')} alt={v.plate} loading="lazy" style={{ height: 36, borderRadius: 4, display: 'block' }}
                               onError={e => { e.currentTarget.replaceWith('—'); }} />
                        </a> : '—'}</td>
                    <td><code>{v.shown}</code>{(v.platePending || 0) > 1 && <span className="small" title="This plate is waiting on several days — picking a non-load reason offers to apply it to all of them" style={{ marginLeft: 4, background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '0 5px' }}>×{v.platePending}</span>}{v.hasVimg && <a href={img(v, 'vehicle')} onClick={e => { e.preventDefault(); setZoom({ images: photos(v), index: v.hasImg ? 1 : 0 }); }} title="Vehicle photo" style={{ marginLeft: 4, cursor: 'zoom-in' }}>🚚</a>}</td>
                    <td>{v.date}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{(v.first || '').slice(0, 5)}{v.last && v.last !== v.first ? '–' + v.last.slice(0, 5) : ''}
                      {(v.tripsOfDay || 1) > 1 && <span className="small" title={'This plate made ' + v.tripsOfDay + ' trips this day; each is matched separately'} style={{ marginLeft: 4, background: '#e0e7ff', color: '#3730a3', borderRadius: 8, padding: '0 5px' }}>trip {v.tripNo}/{v.tripsOfDay}</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {m
                        ? <span style={{ background: m.bg, color: m.fg, borderRadius: 10, padding: '1px 8px', fontWeight: 700, opacity: v.status === 'suggested' ? 0.75 : 1, border: v.status === 'suggested' ? '1px dashed ' + m.fg : 'none' }}
                                title={v.status + (v.confidence != null ? ' · ' + v.confidence + '% confidence' : '')}>
                            {m.icon} {m.label}{v.status === 'suggested' ? '?' : ''}
                          </span>
                        : <span style={{ color: '#b91c1c', fontWeight: 700 }}>❓ needs reason</span>}
                      {v.duplicate && <span className="small" title={'Same truck as ' + (v.sharedWith || v.sameRig || '') + ' — counted once'} style={{ marginLeft: 4, color: '#0f766e', fontWeight: 700 }}>♊ same truck</span>}
                      {v.status === 'unlogged' && <span className="small" title="This trip tipped / took a load but no log entry is left for it" style={{ marginLeft: 4, color: '#b91c1c', fontWeight: 700 }}>📭 not logged</span>}
                      {v.status !== 'needs' && v.status !== 'unlogged' && <span className="small" style={{ color: '#888', marginLeft: 4 }}>{v.status === 'auto' ? 'auto' : v.status === 'suggested' ? '' : '✋'}</span>}
                    </td>
                    <td className="small" style={{ maxWidth: 380 }}>
                      {v.evidence || ''}
                      {v.target && v.reason && !v.targetId && <span style={{ color: '#666' }}> → <code>{v.target}</code></span>}
                      {v.hint && <div style={{ color: '#b45309' }}>⚠ {v.hint}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {v.status === 'suggested' && editing !== key && (
                        <button className="refresh-btn small" style={{ fontWeight: 700 }} onClick={() => accept(v)}>✓ Accept</button>
                      )}
                      {open ? (
                        <span>
                          {ORDER.map(r => (
                            <button key={r} className="refresh-btn small" style={{ marginLeft: 3 }} title={REASON_META[r].label} onClick={() => setReason(v, r)}>
                              {REASON_META[r].icon}
                            </button>
                          ))}
                          <label className="small" style={{ marginLeft: 6 }} title="Remember this plate (Staff / Delivery / Visitor / Cleanup / Not a vehicle) so it is never asked again">
                            <input type="checkbox" checked={!!always[key]} onChange={e => setAlways(a => ({ ...a, [key]: e.target.checked }))} /> always
                          </label>
                          {editing === key && <button className="link" style={{ marginLeft: 4 }} onClick={() => setEditing(null)}>cancel</button>}
                        </span>
                      ) : (
                        <>
                          <button className="link" onClick={() => setEditing(key)}>change</button>
                          {(v.status === 'manual' || v.status === 'accepted' || (v.status === 'unlogged' && /set by /.test(v.evidence || ''))) && <button className="link" style={{ marginLeft: 6 }} onClick={() => undo(v)}>undo</button>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {data && data.matching > limit && (
          <div style={{ display: 'flex', gap: 8, marginTop: '0.5rem', alignItems: 'center' }}>
            <button className="refresh-btn small" disabled={offset === 0 || busy} onClick={() => load(Math.max(0, offset - limit))}>← Prev</button>
            <span className="small">{offset + 1}–{Math.min(offset + limit, data.matching)} of {data.matching}</span>
            <button className="refresh-btn small" disabled={offset + limit >= data.matching || busy} onClick={() => load(offset + limit)}>Next →</button>
          </div>
        )}
        <div className="small" style={{ color: '#888', marginTop: '0.5rem' }}>
          Reason buttons: {ORDER.map(r => REASON_META[r].icon + ' ' + REASON_META[r].label).join(' · ')}. "auto" = clear evidence, ✋ = set by a person, dashed = suggestion, 📭 = tipped / loaded with nothing on the log. Photos show the plate's first read that day.
        </div>
      </div>
    </div>
  );
}
